import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProjectDetailDrawer from "../../components/projects/ProjectDetailDrawer";
import {
  Plus, BarChart3, Calendar, ChevronDown, Code2, MoreVertical, ExternalLink, Info, Flag, History, Brain, FileText,
} from "lucide-react";
import { useBdStore } from "../../store/useBdStore";
import { useBdTargetBonusStore } from "../../store/useBdTargetBonusStore";
import { useBidStore } from "../../store/useBidStore";
import { useClientStore } from "../../store/useClientStore";
import { useProjectStore } from "../../store/useProjectStore";
import { useAuthStore } from "../../store/useAuthStore";
import { useActivityTaskStore } from "../../store/useActivityTaskStore";
import { useUpworkJobsStore } from "../../store/useupworkjobsstore";
import { useProposalStore } from "../../store/useProposalStore";
import { useActionCenterStore } from "../../store/useActionCenterStore";
import { useChatStore } from "../../store/useChatStore";
import { useCommissionStore } from "../../store/useCommissionStore";
import StartActivityModal from "../../components/activities/StartActivityModal";
import { ActionItemRow, ActionCenterModal } from "../../components/dashboard/ActionCenter";
import { MeetingsModal } from "../../components/dashboard/MeetingsModal";
import { MeetingBriefingModal } from "../../components/dashboard/MeetingBriefingModal";
import { MeetingMinutesModal } from "../../components/dashboard/MeetingMinutesModal";
import { ConnectsRecoveryModal } from "../../components/dashboard/ConnectsRecoveryModal";
import { MonthlyAchievementModal } from "../../components/dashboard/MonthlyAchievementModal";
import { TargetHistoryModal } from "../../components/dashboard/TargetHistoryModal";
import type { TargetHistoryRow } from "../../components/dashboard/TargetHistoryModal";
import { axiosInstance } from "../../lib/axios";
import { buildActionItems, ACTION_GROUP_TABS, groupOf, type ActionGroup } from "../../lib/actionCenter";
import { upcomingMeetings, pastMeetingsNeedingMinutes, meetingClient, meetingDateLabel, isToday } from "../../lib/meetings";
import ChecklistRejectionAlert from "../../components/projects/ChecklistRejectionAlert";

const money = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(n || 0).toLocaleString()}`;
};

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <section className={`bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow ${className}`}>
    {children}
  </section>
);
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-[14px] font-semibold text-on-surface uppercase tracking-wider">{children}</h3>
);

// Metric label with a hover explainer, e.g. "Used Units ⓘ".
const MetricLabel = ({ children, tip }: { children: React.ReactNode; tip: string }) => (
  <span className="text-[13px] text-on-surface-variant inline-flex items-center gap-1">
    {children}
    <span className="group relative inline-flex">
      <Info size={12} className="text-outline cursor-help" />
      <span className="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-56 p-2.5 rounded-lg bg-on-surface text-white text-[11px] font-normal normal-case leading-relaxed shadow-xl">
        {tip}
      </span>
    </span>
  </span>
);

interface TargetPeriod {
  label: string;
  target: number;
  achieved: number;
  pct: number;
  remaining: number;
  requiredToStayOnTrack: number;
  pacePct: number;
  status: string;
  bonus: { tierName: string; bonusType: string; bonusValue: number } | null;
}
interface TargetProgress {
  monthly: TargetPeriod;
  quarterly: TargetPeriod;
  yearly: TargetPeriod;
  history: TargetHistoryRow[];
}

const PERIOD_LABEL: Record<string, string> = {
  today: "Today",
  "30d": "Last 30 Days",
  month: "This Month",
  quarter: "This Quarter",
  year: "This Year",
};

// ===== Funnel pipeline (chevron stages) =====
type Stage = { label: string; value: string; bg: string; text: string };
const Funnel = ({ stages }: { stages: Stage[] }) => (
  <div className="flex flex-col md:flex-row items-stretch">
    {stages.map((s, i) => {
      const first = i === 0;
      const last = i === stages.length - 1;
      const clip = first
        ? "polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0% 100%)"
        : last
        ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 5% 50%)"
        : "polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0% 100%, 5% 50%)";
      return (
        <div
          key={i}
          className={`flex-1 p-4 relative ${s.bg}`}
          style={{
            clipPath: clip,
            marginRight: last ? 0 : "-4%",
            paddingLeft: first ? "1.5rem" : "2.5rem",
            paddingRight: last ? "1.5rem" : "2.5rem",
          }}
        >
          <div className={`font-bold text-xs uppercase mb-1 ${s.text}`}>{s.label}</div>
          <div className={`text-[13px] ${s.text}`}>{s.value}</div>
        </div>
      );
    })}
  </div>
);

const Donut = ({ pct }: { pct: number }) => {
  const dash = `${Math.max(0, Math.min(100, pct))}, 100`;
  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full" viewBox="0 0 36 36">
        <path className="text-surface-container" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
        <path className="text-[#10b981]" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeDasharray={dash} strokeLinecap="round" strokeWidth="3" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-on-surface">{pct}%</span>
        <span className="text-[10px] text-outline uppercase">Conv.</span>
      </div>
    </div>
  );
};

export default function BDDashboard() {
  const { overview, fetchOverview, connectsRecovery, fetchConnectsRecovery } = useBdStore();
  const { workspace, fetchWorkspace, monthlyAchievement, fetchMonthlyAchievement } = useBdTargetBonusStore();
  const { bids, fetchMyBids } = useBidStore();
  const { clients, fetchClients } = useClientStore();
  const { projects, fetchProjects } = useProjectStore();
  const { authUser }: any = useAuthStore();
  const { activities, fetchActivities } = useActivityTaskStore();
  const { jobs, fetchJobs } = useUpworkJobsStore();
  const { proposals, fetchProposals } = useProposalStore();
  const { history } = useActionCenterStore();
  const { users: chatUsers, getUsers: getChatUsers, subscribeSidebar, unsubscribeSidebar } = useChatStore();
  const { mySettled, fetchMySettled } = useCommissionStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [meetingsModalOpen, setMeetingsModalOpen] = useState(false);
  const [briefingMeetingId, setBriefingMeetingId] = useState<string | null>(null);
  const [minutesMeeting, setMinutesMeeting] = useState<{ id: string; title: string } | null>(null);
  const [connectsModalOpen, setConnectsModalOpen] = useState(false);
  const [tab, setTab] = useState<"bids" | "leads" | "projects">("bids");
  const [period, setPeriod] = useState("month");
  const [detailProject, setDetailProject] = useState<any>(null);
  const [achievementModalOpen, setAchievementModalOpen] = useState(false);
  const [targetProgress, setTargetProgress] = useState<TargetProgress | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [quoteRequests, setQuoteRequests] = useState<any[]>([]);
  const [actionTab, setActionTab] = useState<ActionGroup>("notifications");
  const navigate = useNavigate();

  // Show the monthly achievement popup once per month, until acknowledged.
  useEffect(() => {
    if (monthlyAchievement?.hasData && monthlyAchievement.key) {
      const ackKey = `cre-monthly-ack-${monthlyAchievement.key}`;
      if (!localStorage.getItem(ackKey)) setAchievementModalOpen(true);
    }
  }, [monthlyAchievement]);
  const closeAchievement = () => {
    if (monthlyAchievement?.key) localStorage.setItem(`cre-monthly-ack-${monthlyAchievement.key}`, "1");
    setAchievementModalOpen(false);
  };

  useEffect(() => {
    fetchOverview(); fetchMyBids(); fetchClients(); fetchProjects();
    fetchActivities(); fetchJobs(); fetchProposals(); getChatUsers();
    fetchConnectsRecovery(); fetchMySettled();
    subscribeSidebar(); fetchWorkspace(); fetchMonthlyAchievement();
    axiosInstance
      .get("/bd-target-bonus/target-progress")
      .then(({ data }) => setTargetProgress(data))
      .catch(() => {});
    axiosInstance
      .get("/service-requests")
      .then(({ data }) => setQuoteRequests(data?.requests || []))
      .catch(() => {});
    return () => unsubscribeSidebar();
  }, []);

  // Real-data action feed, minus anything already acted on (it moves to history).
  const allActionItems = useMemo(
    () => buildActionItems({ conversations: chatUsers, jobs, proposals, projects, overview, leads: clients, quoteRequests, bids }),
    [chatUsers, jobs, proposals, projects, overview, clients, quoteRequests, bids]
  );
  const actionItems = useMemo(
    () => allActionItems.filter((i) => !history[i.id]),
    [allActionItems, history]
  );

  // Scheduled meetings = activities in the "Meeting" category, today or later.
  const meetings = useMemo(() => upcomingMeetings(activities), [activities]);
  const todayMeetings = useMemo(() => meetings.filter((m) => isToday(m.dueDate)), [meetings]);
  // Meetings that already happened but have no minutes yet — prompt the BD to record them.
  const needMinutes = useMemo(() => pastMeetingsNeedingMinutes(activities), [activities]);

  const t = overview?.totals;

  // ── Workspace Analytics time filter ───────────────────────────────────────
  const periodStart = useMemo(() => {
    const now = new Date();
    switch (period) {
      case "today": return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      case "month": return new Date(now.getFullYear(), now.getMonth(), 1);
      case "quarter": return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      case "year": return new Date(now.getFullYear(), 0, 1);
      case "30d":
      default: return new Date(now.getTime() - 30 * 86400000);
    }
  }, [period]);
  const inPeriod = (d?: string) => (d ? new Date(d).getTime() >= periodStart.getTime() : false);
  const fBids = useMemo(() => bids.filter((b: any) => inPeriod(b.createdAt)), [bids, periodStart]);
  const fClients = useMemo(() => clients.filter((c: any) => inPeriod(c.createdAt)), [clients, periodStart]);
  const fProjects = useMemo(() => projects.filter((p: any) => inPeriod(p.createdAt)), [projects, periodStart]);

  // Bidding status (period-filtered, by bid status)
  const WON_BID = new Set(["Awarded Directly", "Successfully Converted"]);
  const LOST_BID = new Set(["Declined / Not Accepted", "Hired Other Freelancer"]);
  const totalBids = fBids.length;
  const wonBids = fBids.filter((b: any) => WON_BID.has(b.bidStatus)).length;
  const lostBids = fBids.filter((b: any) => LOST_BID.has(b.bidStatus)).length;
  const negotiatingBids = fBids.filter((b: any) => b.bidStatus === "In Negotiation").length;
  const periodConnectsUsed = fBids.reduce((s: number, b: any) => s + (Number(b.connectsUsed) || 0), 0);
  const periodConnectsCost = periodConnectsUsed * (connectsRecovery?.costPerConnect || 0);

  // Project financials (period-filtered)
  const projRevenue = (p: any) =>
    p.projectPrice && p.projectPrice > 0 ? p.projectPrice : (p.escrowAmount || 0) + (p.releasedPayment || 0);
  const periodRevenue = fProjects.reduce((s: number, p: any) => s + projRevenue(p), 0);
  const periodReleased = fProjects.reduce((s: number, p: any) => s + (Number(p.releasedPayment) || 0), 0);
  const periodEscrow = fProjects.reduce((s: number, p: any) => s + (Number(p.escrowAmount) || 0), 0);
  // Not yet funded or released — the part of the book still to be collected.
  const periodRemaining = Math.max(0, periodRevenue - periodReleased - periodEscrow);

  // BD commission = ₨1,000 per $100 of revenue (i.e. ₨10 per USD).
  const COMMISSION_PER_USD_PKR = 10;
  const pkr = (n: number) => `₨${Math.round(n || 0).toLocaleString()}`;
  // Commission is a running (all-time) balance, independent of the period filter.
  // Locked = commission on released payments (payable). Payable = Locked − settled
  // by admin. Pending = expected commission on payments not yet released.
  const allReleasedUSD = useMemo(
    () => projects.reduce((s: number, p: any) => s + (Number(p.releasedPayment) || 0), 0),
    [projects]
  );
  const allRevenueUSD = useMemo(
    () => projects.reduce((s: number, p: any) => s + projRevenue(p), 0),
    [projects]
  );
  const commissionLockedPKR = allReleasedUSD * COMMISSION_PER_USD_PKR;
  const commissionPendingPKR = Math.max(0, (allRevenueUSD - allReleasedUSD) * COMMISSION_PER_USD_PKR);

  // Gauge heading names the period concretely — "July 2026" for a month, the
  // filter's own label otherwise.
  const periodTitle = useMemo(() => {
    if (period === "month") {
      return periodStart.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    if (period === "year") return String(periodStart.getFullYear());
    return PERIOD_LABEL[period] || "";
  }, [period, periodStart]);

  // Revenue target gauge — current revenue follows the selected period.
  const targetRevenue = workspace?.metrics?.targetRevenue || 0;
  const currentRevenue = periodRevenue;
  const remainingRevenue = Math.max(0, targetRevenue - currentRevenue);
  // Needle angle: -90° ($0, left) → +90° (target, right)
  const needle = -90 + (Math.min(1, currentRevenue / (targetRevenue || 1))) * 180;
  const activeAssignment = workspace?.assignment;
  const bonusTiers = activeAssignment?.bonusPlan?.tiers || [];

  // Lead pipeline (period-filtered)
  const lc = (s: string) => fClients.filter((c: any) => c.leadStatus === s).length;
  const leadStages: Stage[] = [
    { label: "Pending", value: `${lc("Pending")} Leads`, bg: "bg-surface-container-low", text: "text-on-surface-variant" },
    { label: "Need to Contact", value: `${lc("Need to Contact")} Leads`, bg: "bg-primary", text: "text-on-primary" },
    { label: "Contacted", value: `${lc("Contacted - No Response")} Leads`, bg: "bg-[#10b981]", text: "text-on-primary" },
    { label: "Contact in Future", value: `${lc("Contact in Future")} Leads`, bg: "bg-surface-container", text: "text-on-surface" },
    { label: "Negotiated", value: `${lc("Negotiated")} Leads`, bg: "bg-primary-container", text: "text-on-primary-container" },
    { label: "Closed", value: `${lc("Won Lead")} Won ${lc("Lost Lead")} Lost`, bg: "bg-error-container", text: "text-error" },
  ];
  const wonL = lc("Won Lead"), lostL = lc("Lost Lead");
  const convPct = wonL + lostL ? Math.round((wonL / (wonL + lostL)) * 100) : 0;

  // Project pipeline (period-filtered, loose status match)
  const pCount = (m: string[]) => fProjects.filter((p: any) => m.some((x) => (p.projectStatus || "").toLowerCase().includes(x))).length;
  const pVal = (m: string[]) => fProjects.filter((p: any) => m.some((x) => (p.projectStatus || "").toLowerCase().includes(x))).reduce((s: number, p: any) => s + (p.projectPrice || 0), 0);
  const projStages: Stage[] = [
    { label: "In Queue", value: `${pCount(["queue", "pending", "not"])} ${money(pVal(["queue", "pending", "not"]))}`, bg: "bg-surface-container-low", text: "text-on-surface-variant" },
    { label: "Ongoing", value: `${pCount(["ongoing", "progress", "active"])} ${money(pVal(["ongoing", "progress", "active"]))}`, bg: "bg-primary", text: "text-on-primary" },
    { label: "Completed", value: `${pCount(["complete"])} ${money(pVal(["complete"]))}`, bg: "bg-[#10b981]", text: "text-on-primary" },
    { label: "On Hold", value: `${pCount(["hold"])} ${money(pVal(["hold"]))}`, bg: "bg-surface-container", text: "text-on-surface" },
    { label: "Cancelled", value: `${pCount(["cancel"])} ${money(pVal(["cancel"]))}`, bg: "bg-error-container", text: "text-error" },
  ];

  return (
    <div className="bd-workspace p-6 lg:p-8 flex flex-col gap-6 lg:gap-8 max-w-[1400px] mx-auto w-full">
      {/* Rejected project requirements stay in front of the BD until fixed. */}
      <ChecklistRejectionAlert />
      {/* Header */}
      <Card className="flex flex-col md:flex-row justify-between items-start md:items-center p-6 gap-4">
        <div>
          <h2 className="text-[30px] leading-tight font-bold text-on-surface mb-1">Dashboard</h2>
          <p className="text-[16px] text-on-surface-variant">Start a new activity to create and track a task through the ERP system.</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="bg-primary text-white text-sm font-semibold px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-[#004b9e] transition-colors shrink-0">
          <Plus size={18} /> Start Activity
        </button>
      </Card>

      {/* Analytics */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 pb-4 border-b border-outline-variant gap-4">
          <div className="flex items-center gap-2">
            <BarChart3 size={20} className="text-primary" />
            <div>
              <h4 className="text-sm font-semibold text-on-surface">Workspace Analytics</h4>
              <p className="text-[11px] font-semibold text-on-surface-variant">Metrics apply to Revenue, Performance, Leads, Bids, and Projects</p>
            </div>
          </div>
          <div className="relative min-w-[180px]">
            <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full pl-9 pr-9 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-xs font-medium text-on-surface focus:ring-2 focus:ring-primary outline-none appearance-none cursor-pointer"
            >
              <option value="30d">Last 30 Days</option>
              <option value="today">Today</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <ChevronDown size={18} className="absolute right-2 top-1/2 -translate-y-1/2 text-outline pointer-events-none" />
          </div>
        </div>

        {/* Quarterly target banner */}
        {targetProgress && (() => {
          const q = targetProgress.quarterly;
          // One decimal (the API rounds to int, so recompute from the raw figures).
          const qPctExact = q.target > 0 ? ((q.achieved / q.target) * 100).toFixed(1) : "0.0";
          const st = q.status === "Target Achieved" || q.status === "On Track"
            ? { dot: "bg-white/80", text: "text-white", chip: "bg-[#10b981] border-[#10b981]" }
            : q.status === "Slightly Behind"
            ? { dot: "bg-white/80", text: "text-white", chip: "bg-amber-500 border-amber-500" }
            : q.status === "No Target"
            ? { dot: "bg-white/80", text: "text-white", chip: "bg-outline border-outline" }
            : { dot: "bg-white/80", text: "text-white", chip: "bg-error border-error" };
          return (
            <div className="mb-6 p-4 bg-[#eff4ff] border border-[#dbe7ff] rounded-xl flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Left: label + achievement */}
              <div className="flex items-center gap-2 shrink-0 lg:w-64">
                <Flag size={18} className="text-primary shrink-0" />
                <div>
                  <h5 className="text-sm font-semibold text-on-surface">
                    Quarterly Target: {q.label}
                  </h5>
                  <p className="text-[11px] text-on-surface-variant">
                    Total Achievement:{" "}
                    <span className="font-bold text-on-surface">${q.achieved.toLocaleString()}</span>{" "}
                    / ${q.target.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Middle: full-width progress bar with pace marker */}
              <div className="flex-grow flex items-center gap-3 min-w-0">
                <div className="relative flex-grow h-2.5 bg-surface-container rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(100, q.pct)}%` }}
                  />
                  {q.target > 0 && q.pacePct > 0 && q.pacePct < 100 && (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-on-surface/50 rounded"
                      style={{ left: `${q.pacePct}%` }}
                      title={`Expected pace: ${q.pacePct}%`}
                    />
                  )}
                </div>
                <span className="text-sm font-bold text-primary shrink-0 w-14 text-right" title={`Remaining $${q.remaining.toLocaleString()} · to pace $${q.requiredToStayOnTrack.toLocaleString()}`}>
                  {qPctExact}%
                </span>
              </div>

              {/* Right: status + history */}
              <div className="flex items-center gap-4 shrink-0">
                <span className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase px-2.5 py-1 rounded-full border ${st.chip} ${st.text}`}>
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} /> {q.status}
                </span>
                <button
                  onClick={() => setHistoryOpen(true)}
                  className="text-primary text-xs font-semibold hover:underline flex items-center gap-1 shrink-0"
                >
                  View History <History size={13} />
                </button>
              </div>
            </div>
          );
        })()}

        <div className="flex flex-col md:flex-row gap-8">
          {/* Gauge + bonus */}
          <div className="flex-1 flex flex-col">
            <div className="mb-4">
              <SectionTitle>Revenue Target — {periodTitle}</SectionTitle>
            </div>
            <div className="flex flex-col gap-4 items-center">
              <div className="relative flex flex-col items-center">
                <svg className="w-full max-w-[260px]" viewBox="0 0 200 130">
                  <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#e5eeff" strokeLinecap="round" strokeWidth="12" />
                  <path d="M 20 100 A 80 80 0 0 1 65 35" fill="none" stroke="#ba1a1a" strokeOpacity="0.85" strokeWidth="12" />
                  <path d="M 65 35 A 80 80 0 0 1 135 35" fill="none" stroke="#facc15" strokeOpacity="0.9" strokeWidth="12" />
                  <path d="M 135 35 A 80 80 0 0 1 180 100" fill="none" stroke="#10b981" strokeOpacity="0.9" strokeWidth="12" />
                  <g fill="#727786" fontFamily="Inter" fontSize="9" textAnchor="middle">
                    <text x="12" y="112">$0</text><text x="16" y="72">$500</text><text x="32" y="42">$1k</text>
                    <text x="66" y="20">$2k</text><text x="100" y="10">$4k</text><text x="134" y="20">$8k</text>
                    <text x="168" y="42">$16k</text><text x="188" y="112">{money(targetRevenue || 25000)}</text>
                  </g>
                  <g transform={`rotate(${needle} 100 100)`}>
                    <path d="M 100 33 L 96 46 L 104 46 Z" fill="#0b1c30" />
                  </g>
                </svg>
                <div className="absolute flex flex-col items-center" style={{ top: "48%" }}>
                  <span className="text-2xl font-bold text-on-surface">{money(currentRevenue)}</span>
                  <span className="text-[11px] font-semibold text-secondary uppercase">Current Revenue</span>
                </div>
              </div>
              {/* Bonus tiers — horizontal row under the gauge */}
              <div className="flex flex-col items-center gap-1.5 w-full">
                <span className="text-[11px] text-outline uppercase">Bonus</span>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {bonusTiers.length > 0 ? bonusTiers.map((tier, idx) => (
                    <span key={tier._id || idx} className={`${idx === 0 ? "bg-primary text-white" : "bg-surface-container text-on-surface-variant"} text-[10px] px-2.5 py-0.5 rounded-full whitespace-nowrap`}>
                      {tier.tierName} {tier.bonusType === "Percentage" ? `${tier.bonusValue}%` : `${tier.bonusValue.toLocaleString()} PKR`}
                    </span>
                  )) : (
                    <span className="bg-surface-container text-on-surface-variant text-[10px] px-2.5 py-0.5 rounded-full whitespace-nowrap">No bonus plan assigned</span>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 divide-x divide-outline-variant border-t border-outline-variant pt-3 mt-3 w-full text-center">
              <div className="flex flex-col"><span className="text-[11px] font-semibold text-outline uppercase">Target</span><span className="text-base font-bold text-on-surface">{money(targetRevenue)}</span></div>
              <div className="flex flex-col"><span className="text-[11px] font-semibold text-outline uppercase">Remaining</span><span className="text-base font-bold text-on-surface">{money(remainingRevenue)}</span></div>
              <div className="flex flex-col"><span className="text-[11px] font-semibold text-outline uppercase">Required</span><span className="text-base font-bold text-primary">{money(remainingRevenue)}</span></div>
            </div>
          </div>

          <div className="hidden md:block w-px bg-outline-variant self-stretch" />

          {/* Performance metrics */}
          <div className="flex-[1.5] flex flex-col">
            <SectionTitle>Performance Metrics</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 mb-4">
              <div className="p-3 bg-surface-bright border border-outline-variant rounded-lg flex flex-col">
                <div className="text-[11px] font-semibold text-outline uppercase mb-2 text-center">Bidding Status</div>
                <div className="text-center mb-3">
                  <div className="text-sm text-on-surface-variant">Total Bids</div>
                  <div className="text-[28px] leading-tight font-bold text-on-surface">{totalBids}</div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[11px] font-semibold mt-auto">
                  <span className="text-primary bg-[#eff4ff] px-2 py-1 rounded-full text-center">{wonBids} Won</span>
                  <span className="text-error bg-error-container px-2 py-1 rounded-full text-center">{lostBids} Lost</span>
                  <span className="col-span-2 text-amber-700 bg-amber-100 px-2 py-1 rounded-full text-center">{negotiatingBids} Neg</span>
                </div>
              </div>
              <div className="p-3 bg-surface-bright border border-outline-variant rounded-lg flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold text-outline uppercase">Connect Usage</div>
                  <button onClick={() => setConnectsModalOpen(true)} title="Connects recovery history" className="text-outline hover:text-primary">
                    <ExternalLink size={13} />
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <MetricLabel tip={`Connects you spent on bids in ${PERIOD_LABEL[period]}.`}>Used Units</MetricLabel>
                    <span className="text-sm font-bold text-on-surface">{periodConnectsUsed}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <MetricLabel tip="Cost of those connects at the current per-connect rate.">Cost</MetricLabel>
                    <span className="text-sm font-bold text-on-surface">{money(periodConnectsCost)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-outline-variant pt-2">
                    <MetricLabel tip="Connect cost already earned back through won projects.">Recovered</MetricLabel>
                    <span className="text-sm font-semibold text-[#10b981]">{money(connectsRecovery?.totalRecovered || 0)}</span>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-surface-bright border border-outline-variant rounded-lg flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold text-outline uppercase">Commission</div>
                  <button onClick={() => navigate("/profile")} title="Commission details" className="text-outline hover:text-primary">
                    <ExternalLink size={13} />
                  </button>
                </div>
                <div className="flex flex-col">
                  <MetricLabel tip={`Total commission you've earned on released project payments. Rate: ₨1,000 per $100 of revenue.`}>Earned</MetricLabel>
                  <div className="text-[26px] leading-tight font-bold text-primary mb-1.5">{pkr(commissionLockedPKR)}</div>
                  <div className="flex justify-between items-center">
                    <MetricLabel tip="Commission already settled and paid out to you by Admin.">Paid</MetricLabel>
                    <span className="text-sm font-bold text-[#10b981]">{pkr(mySettled || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <MetricLabel tip="Expected commission on project payments that have not been released yet.">Pending</MetricLabel>
                    <span className="text-sm font-bold text-on-surface">{pkr(commissionPendingPKR)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 bg-surface-bright border border-outline-variant rounded-lg flex flex-col gap-2">
              <div>
                <div className="text-[11px] font-semibold text-outline uppercase mb-1">Project Financials</div>
                <div className="flex justify-between items-center">
                  <span className="text-base font-bold text-on-surface">Total Value</span>
                  <span className="text-[26px] leading-tight font-bold text-on-surface">{money(periodRevenue)}</span>
                </div>
              </div>
              <div className="space-y-1 border-t border-outline-variant pt-2">
                <div className="flex justify-between items-center"><span className="text-[13px] text-on-surface-variant">Released</span><span className="text-[13px] font-semibold text-[#10b981]">{money(periodReleased)}</span></div>
                <div className="flex justify-between items-center"><span className="text-[13px] text-on-surface-variant">Escrow</span><span className="text-[13px] font-semibold text-primary">{money(periodEscrow)}</span></div>
                <div className="flex justify-between items-center"><span className="text-[13px] text-on-surface-variant">Remaining</span><span className="text-[13px] font-semibold text-outline">{money(periodRemaining)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Center + Meetings */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        <Card className="md:col-span-2 !p-0 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-outline-variant">
            <h2 className="text-[13px] font-bold text-tertiary uppercase tracking-wider">Action Center</h2>
          </div>
          {/* Full-width tabs */}
          <div className="flex border-b border-outline-variant bg-surface-container-low">
            {ACTION_GROUP_TABS.map((t) => {
              const count = actionItems.filter((i) => groupOf(i) === t.key).length;
              return (
                <button
                  key={t.key}
                  onClick={() => setActionTab(t.key)}
                  className={`flex-1 px-3 py-3 text-[13px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                    actionTab === t.key ? "text-primary border-primary" : "text-on-surface-variant border-transparent hover:text-primary"
                  }`}
                >
                  {t.label} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
          {/* Items */}
          <div className="p-4 flex flex-col gap-2 flex-grow">
            {(() => {
              const tabItems = actionItems.filter((i) => groupOf(i) === actionTab);
              return tabItems.length === 0 ? (
                <div className="text-center py-8 text-outline text-sm">Nothing here right now — you're all caught up.</div>
              ) : (
                tabItems.slice(0, 5).map((item) => <ActionItemRow key={item.id} item={item} />)
              );
            })()}
          </div>
          {/* Footer */}
          <div className="px-4 py-3 text-center border-t border-outline-variant mt-auto">
            <button onClick={() => setActionsModalOpen(true)} className="text-primary text-[13px] font-medium hover:underline">View All Activities →</button>
          </div>
        </Card>
        <Card className="md:col-span-1 p-4 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <SectionTitle>Scheduled Meetings</SectionTitle>
            <div className="text-[11px] font-semibold text-on-surface-variant">Today: <span className="font-bold text-primary">{todayMeetings.length}</span></div>
          </div>
          {needMinutes.length > 0 && (
            <div className="mb-3 rounded-lg border border-secondary/40 bg-secondary-container/40 p-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <FileText size={14} className="text-secondary" />
                <span className="text-[12px] font-bold text-on-secondary-container">Record minutes ({needMinutes.length})</span>
              </div>
              <div className="flex flex-col gap-1.5">
                {needMinutes.slice(0, 3).map((m) => (
                  <div key={m._id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-on-surface truncate">{m.title || m.description || "Meeting"}</div>
                      <div className="text-[11px] text-on-surface-variant">{meetingDateLabel(m.dueDate)}{meetingClient(m) ? ` · ${meetingClient(m)}` : ""}</div>
                    </div>
                    <button onClick={() => setMinutesMeeting({ id: m._id, title: m.title || m.description || "Meeting" })} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-secondary hover:underline">
                      <FileText size={12} /> Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2 flex-grow">
            {meetings.length === 0 ? (
              <div className="text-center py-8 text-outline text-sm">No upcoming meetings. Add an activity in the “Meeting” category to schedule one.</div>
            ) : (
              meetings.slice(0, 4).map((m) => (
                <div key={m._id} className="p-2.5 bg-surface-bright border border-outline-variant rounded-lg">
                  <div className="flex items-center gap-1 mb-1"><Calendar size={16} className="text-primary" /><span className="text-sm font-semibold text-on-surface truncate">{m.title || m.description || "Meeting"}</span></div>
                  {meetingClient(m) && (
                    <div className="text-[13px] text-on-surface-variant mb-1 truncate">Client: <span className="font-medium">{meetingClient(m)}</span></div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <div className={`text-xs font-bold ${isToday(m.dueDate) ? "text-primary" : "text-on-surface-variant"}`}>{meetingDateLabel(m.dueDate)}</div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setBriefingMeetingId(m._id)} className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline">
                        <Brain size={12} /> Prep
                      </button>
                      <button onClick={() => setMinutesMeeting({ id: m._id, title: m.title || m.description || "Meeting" })} className="inline-flex items-center gap-1 text-[11px] font-bold text-secondary hover:underline">
                        <FileText size={12} /> Minutes
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {meetings.length > 0 && (
            <button onClick={() => setMeetingsModalOpen(true)} className="mt-4 text-primary text-xs font-medium hover:underline text-center">View All Meetings →</button>
          )}
        </Card>
      </section>

      {/* Consolidated tabs */}
      <Card className="p-4 flex flex-col">
        <div className="flex border-b border-outline-variant mb-4 gap-4">
          {(["bids", "leads", "projects"] as const).map((tk) => (
            <button key={tk} onClick={() => setTab(tk)} className={`px-4 py-2 text-sm font-semibold capitalize transition-colors ${tab === tk ? "text-primary border-primary border-b-2" : "text-on-surface-variant"}`}>{tk}</button>
          ))}
        </div>

        {/* BIDS */}
        {tab === "bids" && (
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
            <div className="lg:col-span-7">
              <div className="flex justify-between items-center mb-3"><SectionTitle>Recent Bid Activity</SectionTitle><button onClick={() => navigate("/bids")} className="text-xs text-primary font-medium hover:underline">View All</button></div>
              <div className="overflow-x-auto cre-scroll">
                <table className="w-full text-left border-collapse">
                  <thead><tr className="border-b border-outline-variant">{["Job Title", "Account", "Connects", "Amount", "AI Score", "Status"].map((h) => <th key={h} className="py-2 text-[11px] font-semibold text-outline uppercase">{h}</th>)}</tr></thead>
                  <tbody className="text-[13px] text-on-surface">
                    {fBids.slice(0, 6).map((b: any, i: number) => {
                      const score = 95 - i * 9;
                      return (
                        <tr key={b._id} onClick={() => navigate("/bids")} className="border-b border-outline-variant hover:bg-surface-container-low cursor-pointer">
                          <td className="py-3 font-medium">{b.jobTitle || "Untitled"}</td>
                          <td className="py-3">{b.platformAccount || "-"}</td>
                          <td className="py-3">{b.connectsUsed ?? 0}</td>
                          <td className="py-3 font-semibold">{money(b.bidAmount || 0)}</td>
                          <td className="py-3"><span className={score >= 75 ? "text-[#10b981] font-bold" : "text-error font-bold"}>{score}%</span></td>
                          <td className="py-3"><span className="bg-surface-container text-on-surface-variant px-2 py-0.5 rounded-full text-[10px] uppercase font-bold">{b.bidStatus || "Open"}</span></td>
                        </tr>
                      );
                    })}
                    {fBids.length === 0 && <tr><td colSpan={6} className="py-3 text-outline text-center">No bids in this period.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="lg:col-span-3">
              <SectionTitle>Top Performing Category</SectionTitle>
              <div className="bg-surface-bright border border-outline-variant rounded-lg p-4 my-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary grid place-items-center text-white"><Code2 size={26} /></div>
                <div className="text-lg font-semibold text-on-surface">Web Development</div>
              </div>
              <div className="grid gap-2">
                <div className="flex justify-between items-center p-2.5 bg-surface-container-low rounded-lg"><span className="text-[13px] text-on-surface-variant">Win Rate</span><span className="text-sm font-semibold text-[#10b981]">{t?.winRate ?? 0}%</span></div>
                <div className="flex justify-between items-center p-2.5 bg-surface-container-low rounded-lg"><span className="text-[13px] text-on-surface-variant">Projects Won</span><span className="text-sm font-semibold text-on-surface">{wonBids}</span></div>
                <div className="flex justify-between items-center p-2.5 bg-surface-container-low rounded-lg"><span className="text-[13px] text-on-surface-variant">Revenue</span><span className="text-sm font-semibold text-primary">{money(periodRevenue)}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* LEADS */}
        {tab === "leads" && (
          <div className="flex flex-col gap-6">
            <div><SectionTitle>Leads Pipeline</SectionTitle><div className="mt-3"><Funnel stages={leadStages} /></div></div>
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
              <div className="lg:col-span-7">
                <div className="flex justify-between items-center mb-3"><SectionTitle>Recent Leads</SectionTitle><button onClick={() => navigate("/clients")} className="text-xs text-primary font-medium hover:underline">View All</button></div>
                <div className="overflow-x-auto cre-scroll">
                  <table className="w-full text-left border-collapse">
                    <thead><tr className="border-b border-outline-variant">{["Client", "Company", "Lead Status", "Source", "Created", ""].map((h, i) => <th key={i} className="py-2 text-[11px] font-semibold text-outline uppercase">{h}</th>)}</tr></thead>
                    <tbody className="text-[13px] text-on-surface">
                      {fClients.slice(0, 6).map((c: any) => (
                        <tr key={c._id} onClick={() => navigate("/clients")} className="border-b border-outline-variant hover:bg-surface-container-low cursor-pointer">
                          <td className="py-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-surface-container grid place-items-center text-primary font-bold text-[11px]">{(c.name || "?").slice(0, 2).toUpperCase()}</div><span className="font-medium">{c.name}</span></div></td>
                          <td className="py-3">{c.company || "-"}</td>
                          <td className="py-3"><span className="bg-primary-container text-on-primary-container px-2 py-0.5 rounded-full text-[10px] uppercase font-bold">{c.leadStatus || "Pending"}</span></td>
                          <td className="py-3">{c.leadSource || "-"}</td>
                          <td className="py-3 text-outline">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "-"}</td>
                          <td className="py-3"><button className="p-1 text-on-surface-variant hover:text-primary"><MoreVertical size={16} /></button></td>
                        </tr>
                      ))}
                      {fClients.length === 0 && <tr><td colSpan={6} className="py-3 text-outline text-center">No leads in this period.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="lg:col-span-3">
                <SectionTitle>Leads Performance</SectionTitle>
                <div className="bg-surface-bright border border-outline-variant rounded-lg p-4 mt-3 flex flex-col items-center">
                  <Donut pct={convPct} />
                  <div className="w-full space-y-1 mt-4">
                    <div className="flex justify-between"><span className="text-[11px] text-on-surface-variant">Total Leads</span><span className="text-[11px] font-bold">{fClients.length}</span></div>
                    <div className="flex justify-between"><span className="text-[11px] text-secondary">Won</span><span className="text-[11px] font-bold">{wonL}</span></div>
                    <div className="flex justify-between"><span className="text-[11px] text-error">Loss</span><span className="text-[11px] font-bold">{lostL}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PROJECTS */}
        {tab === "projects" && (
          <div className="flex flex-col gap-6">
            <div><SectionTitle>Project Pipeline</SectionTitle><div className="mt-3"><Funnel stages={projStages} /></div></div>
            <div>
              <div className="flex justify-between items-center mb-3"><SectionTitle>Recent Project Activity</SectionTitle><button onClick={() => navigate("/projects")} className="text-xs text-primary font-medium hover:underline">View All</button></div>
              <div className="overflow-x-auto cre-scroll">
                <table className="w-full text-left border-collapse">
                  <thead><tr className="border-b border-outline-variant">{["Project", "Status", "Total", "Escrow", "Updated"].map((h) => <th key={h} className="py-2 text-[11px] font-semibold text-outline uppercase">{h}</th>)}</tr></thead>
                  <tbody className="text-[13px] text-on-surface">
                    {fProjects.slice(0, 6).map((p: any) => (
                      <tr key={p._id} onClick={() => setDetailProject(p)} className="border-b border-outline-variant hover:bg-surface-container-low cursor-pointer">
                        <td className="py-3 font-medium">{p.name}</td>
                        <td className="py-3"><span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-primary" />{p.projectStatus || "-"}</span></td>
                        <td className="py-3 font-semibold">{money(p.projectPrice || 0)}</td>
                        <td className="py-3">{money(p.escrowAmount || 0)}</td>
                        <td className="py-3 text-outline">{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "-"}</td>
                      </tr>
                    ))}
                    {fProjects.length === 0 && <tr><td colSpan={5} className="py-3 text-outline text-center">No projects in this period.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Card>

      <StartActivityModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      {actionsModalOpen && (
        <ActionCenterModal items={actionItems} onClose={() => setActionsModalOpen(false)} />
      )}
      {meetingsModalOpen && (
        <MeetingsModal meetings={meetings} onClose={() => setMeetingsModalOpen(false)} />
      )}
      {briefingMeetingId && (
        <MeetingBriefingModal meetingId={briefingMeetingId} onClose={() => setBriefingMeetingId(null)} />
      )}
      {minutesMeeting && (
        <MeetingMinutesModal meetingId={minutesMeeting.id} meetingTitle={minutesMeeting.title} onClose={() => setMinutesMeeting(null)} />
      )}
      {connectsModalOpen && (
        <ConnectsRecoveryModal data={connectsRecovery} onClose={() => setConnectsModalOpen(false)} />
      )}
      {detailProject && (
        <ProjectDetailDrawer project={detailProject} onClose={() => setDetailProject(null)} />
      )}
      {achievementModalOpen && monthlyAchievement && (
        <MonthlyAchievementModal data={monthlyAchievement} onClose={closeAchievement} />
      )}
      {historyOpen && targetProgress && (
        <TargetHistoryModal rows={targetProgress.history} onClose={() => setHistoryOpen(false)} />
      )}
    </div>
  );
}
